import * as moment from 'moment'
import * as findIndex from 'lodash/findIndex'
import * as findLastIndex from 'lodash/findLastIndex'
import * as last from 'lodash/last'
import * as range from 'lodash/range'
import { NumericMap } from 'interfaces'
import { Item, Dot, LineType, LineChartInnerOwnProps, Tooltip } from 'components/features/pfm/LineChart/LineChart'
import { isTimeframeWithToday } from 'helpers/dateTimeHelpers'
import { DATE_FORMAT_ISO_FULL } from 'constants/datetime'
import { createTimeperiod, getMonthsCount } from 'helpers/dateTimeHelpers'
import { LineChartInnerStateProps } from 'components/features/pfm/LineChart/LineChart'
import { TrendLine, DateBasedAmount, Trend } from 'interfaces/trend'
import { ScaleFunction, Boundaries, Timeframe, Timeperiod } from 'interfaces/chart'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { AccountType } from 'interfaces/accounts'
import { GenericTrendType } from 'interfaces/pages'

export type DateXMap = Record<string, number>

const interpolateTrend = (trendLine: TrendLine, xMap: DateXMap): TrendLine => {
  const len = trendLine.length
  if (len === 0) {
    return []
  }

  const getDelta = (prev: DateBasedAmount, next: DateBasedAmount) =>
    (prev.amount - next.amount) / (xMap[prev.date] - xMap[next.date])

  const firstDate = trendLine[0].date
  const lastDate = last(trendLine).date
  const lastDateMoment = moment(lastDate)

  let prevPointIndex = 0
  let nextPointIndex = 1
  let prevPoint = trendLine[prevPointIndex]
  let nextPoint = trendLine[nextPointIndex]
  let delta = getDelta(prevPoint, nextPoint)
  let lastY = prevPoint.amount

  const result: TrendLine = []
  for (
    const currentDate = moment(firstDate);
    currentDate.valueOf() <= lastDateMoment.valueOf();
    currentDate.add(1, 'day')
  ) {
    const date = currentDate.format(DATE_FORMAT_ISO_FULL)
    const x = xMap[date]

    // update anchor points if reach next one
    if (date === nextPoint.date) {
      prevPointIndex = nextPointIndex
      prevPoint = nextPoint

      // do not update if this is last one
      if (currentDate.valueOf() < lastDateMoment.valueOf()) {
        nextPointIndex++
        nextPoint = trendLine[nextPointIndex]

        delta = getDelta(prevPoint, nextPoint)
      }
    }

    lastY = x === xMap[prevPoint.date] ?
      prevPoint.amount :
      lastY + delta

    result.push({
      date,
      amount: lastY,
    })
  }

  return result
}

export const approximatePoint = (firstPoint: number, lastPoint: number, pointNum: number, distance: number): number => {
  const delta = (lastPoint - firstPoint) / distance
  return firstPoint + pointNum * delta
}

const buildApproximatedPeersTrend = (trendLine: TrendLine, timeperiod: Timeperiod, xMap: DateXMap, today: string | null): TrendLine => {
  const firstIndex = findIndex(trendLine, { date: timeperiod.start_date })
  const lastIndex = findIndex(trendLine, { date: timeperiod.end_date })
  const prevPoint = trendLine[firstIndex - 1]
  const filteredPoints = trendLine.slice(firstIndex, lastIndex + 1)

  const firstFilteredPoint = filteredPoints[0]
  const firstItem: DateBasedAmount = {
    date: moment(timeperiod.start_date).startOf('month').format(DATE_FORMAT_ISO_FULL),
    // we have data for previous date, so we should approximate value
    amount: approximatePoint(prevPoint.amount, firstFilteredPoint.amount, 1, moment(firstFilteredPoint.date).daysInMonth()),
  }

  const restItems: DateBasedAmount[] = filteredPoints.map((point) => {
    const date = moment(point.date).endOf('month').format(DATE_FORMAT_ISO_FULL)

    return {
      date,
      amount: point.amount,
    }
  })

  // we shouldn't always show data for last day of current month
  // so we should recalculate it approximately
  const middleItems = restItems.slice(0, -1)
  const lastItem: DateBasedAmount = today
    ? {
      date: today,
      amount: last(filteredPoints).amount,
    }
    : last(restItems)

  const points = [firstItem, ...middleItems, lastItem]
  return interpolateTrend(points, xMap)
}

/**
 * This solution does not consider that chart can have changeable width
 * Be careful with that
 */
const getCollidedDistance = ({ start_date, end_date }: Timeperiod) => {
  const months = moment(end_date).diff(moment(start_date), 'months') + 1
  const distance = Math.floor(months / 2)

  return distance
}

// pass only trend for current timeframe here
export const buildDots = (trendLine: TrendLine, type: LineType, xMap: DateXMap, timeperiod: Timeperiod): Dot[] => {
  const lastItemIdx = trendLine.length - 1
  const dotPoints = trendLine.filter((item, idx) => {
    if (idx === lastItemIdx) {
      return true
    }

    const endOfMonth = moment(item.date).endOf('month').format(DATE_FORMAT_ISO_FULL)
    return endOfMonth === item.date
  })

  const filteredDottedPoints = dotPoints.length > 1 ?
    (() => {
      const collidedDistance = getCollidedDistance(timeperiod)
      const lastItem = dotPoints.slice(-1)[0]
      const prelastItem = dotPoints.slice(-2, -1)[0]
      const diffDays = moment(lastItem.date).diff(
        moment(prelastItem.date),
        'days'
      )

      if (collidedDistance >= diffDays) {
        return dotPoints.filter((item) => item !== prelastItem)
      }

      return dotPoints
    })() :
    dotPoints

  const dots = filteredDottedPoints.map(({ date, amount }): Dot => ({
    x: xMap[date],
    y: amount,
    type,
  }))

  return dots
}

export const buildPoints = (filteredPoints: TrendLine, xMap: DateXMap): Item[] =>
  filteredPoints.map((point) => ({
    x: xMap[point.date],
    y: point.amount,
  }))

/**
 * Builds map { date -> x } for help to build other things
 */
export const buildDatesXMap = (timeperiod: Timeperiod): DateXMap => {
  const firstDay = moment(timeperiod.start_date).startOf('month')
  const lastDay = moment(timeperiod.end_date).endOf('month')

  const map = {} as DateXMap
  for (
    let x = 1, currentDay = moment(firstDay);
    currentDay.valueOf() < lastDay.valueOf();
    currentDay.add(1, 'day'), x++
  ) {
    const date = currentDay.format(DATE_FORMAT_ISO_FULL)
    map[date] = x
  }

  return map
}

// when we show last month, we can't show today anyway
export const calculateToday = (trendLine: TrendLine, timeframe: Timeframe): string | null => {
  if (!isTimeframeWithToday(timeframe)) {
    return null
  }

  return last(trendLine).date
}

type FormatDateFunction = (date: string) => string
export const buildTips = (xMap: DateXMap, today: string | null, format: string | FormatDateFunction = 'Do'): NumericMap<string> => {
  const dates = Object.keys(xMap)

  const tips: NumericMap<string> = {}
  const now = moment().format(DATE_FORMAT_ISO_FULL)
  for (const date of dates) {
    const x = xMap[date]
    const tip = date === today && date === now
      ? 'TODAY'
      : (typeof format === 'function' ? format(date) : moment(date).format(format))
    tips[x] = tip
  }

  return tips
}

export const formatHeaderTipDate: FormatDateFunction = (date: string): string => {
  const parsedDate = moment(date)
  return `${ parsedDate.format('MMMM').toUpperCase() } ${ parsedDate.format('Do') }`
}

export const buildDividersOneMonth = (xMap: DateXMap): number[] => {
  const dates = Object.keys(xMap)
  const firstDay = dates[0]
  const lastDay = last(dates)

  const firstX = xMap[firstDay]
  const restX = [] as number[]
  const lastX = xMap[lastDay]

  for (
    const currentDate = moment(firstDay).date(5),
      lastDate = moment(lastDay).endOf('month');
    currentDate.valueOf() <= lastDate.valueOf();
    currentDate.add(5, 'day')
  ) {
    const daysRemain = currentDate.daysInMonth() - currentDate.date()
    if (daysRemain >= 3) {
      const dateStr = currentDate.format(DATE_FORMAT_ISO_FULL)
      const x = xMap[dateStr]
      restX.push(x)
    }
  }

  return [firstX, ...restX, lastX]
}

const buildDividersMultiMonth = (xMap: DateXMap): number[] => {
  const dates = Object.keys(xMap)
  const firstDay = dates[0]
  const lastDay = last(dates)

  const firstX = xMap[firstDay]
  const restX = [] as number[]

  for (
    const currentDate = moment(firstDay).endOf('month'),
      lastDate = moment(lastDay).endOf('month');
    currentDate.valueOf() <= lastDate.valueOf();
    currentDate.add(1, 'month').endOf('month')
  ) {
    const dateStr = currentDate.format(DATE_FORMAT_ISO_FULL)
    const x = xMap[dateStr]
    restX.push(x)
  }

  return [firstX, ...restX]
}

export const buildSectionsOneMonth = (xMap: DateXMap): NumericMap<string> => {
  const dates = Object.keys(xMap)
  const firstDay = dates[0]
  const lastDay = dates[dates.length - 1]
  const firstX = xMap[firstDay]
  const lastX = xMap[lastDay]

  const sections: NumericMap<string> = {
    [firstX]: moment(firstDay).format('Do'),
    [lastX]: moment(lastDay).format('Do'),
  }

  for (
    const currentDate = moment(firstDay).date(5),
      lastDate = moment(lastDay).endOf('month');
    currentDate.valueOf() <= lastDate.valueOf();
    currentDate.add(5, 'day')
  ) {
    const daysRemain = currentDate.daysInMonth() - currentDate.date()
    if (daysRemain >= 3) {
      const dateStr = currentDate.format(DATE_FORMAT_ISO_FULL)
      const x = xMap[dateStr]
      sections[x] = currentDate.format('Do')
    }
  }

  return sections
}

const buildSectionsMultiMonth = (xMap: DateXMap): NumericMap<string> => {
  const dates = Object.keys(xMap)
  const firstDay = dates[0]
  const lastDay = dates[dates.length - 1]

  const sections: NumericMap<string> = {}

  for (
    const currentDate = moment(firstDay).set('date', 15), lastDate = moment(lastDay);
    currentDate.valueOf() < lastDate.valueOf();
    currentDate.add(1, 'month')
  ) {
    const dateStr = currentDate.format(DATE_FORMAT_ISO_FULL)
    const x = xMap[dateStr]
    sections[x] = currentDate.format('MMM').toUpperCase()
  }

  return sections
}

// TODO pick by timeframe somewhen
export const getMonthlyTrendChanges = (trendLine: TrendLine) => trendLine.slice(1).map(
  ({ amount, date }: DateBasedAmount, idx: number) => ({ amount: amount - trendLine[idx].amount, date })
)

const pickTimeframeTrend = (trendLine: TrendLine, timeperiod: Timeperiod): TrendLine => {
  const firstDay = moment(timeperiod.start_date).startOf('month').format(DATE_FORMAT_ISO_FULL)
  const lastDay = moment(timeperiod.end_date).endOf('month').format(DATE_FORMAT_ISO_FULL)

  const firstIndex = findIndex(trendLine, { date: firstDay })
  const lastFoundIndex = findIndex(trendLine, { date: lastDay })
  const lastIndex = lastFoundIndex > 0 ? lastFoundIndex : trendLine.length - 1

  return trendLine.slice(firstIndex, lastIndex + 1)
}

const creditScoreValueDecorator = (value: number) =>
  String(Math.round(value))

type LineTrendOwnProps = LineChartInnerOwnProps & {
  type: AccountType | GenericTrendType
  timeframe: Timeframe
}
type LineChartTrends = {
  memberTrend: Trend
  peersTrend: Trend
  othersTrend: Trend
}
export const prepareTrendProps = (
  trends: LineChartTrends,
  ownProps: LineTrendOwnProps,
  selectedPeergroupName: string,
  isMobile: boolean,
  withComparison: boolean,
): LineChartInnerStateProps => {
  const { type, timeframe } = ownProps
  const { memberTrend, peersTrend, othersTrend } = trends

  const memberActual = memberTrend.actual
  const peersActual = peersTrend.actual
  const othersActual = othersTrend.actual

  const isOneMonth = getMonthsCount(timeframe) === 1
  const timeperiod = createTimeperiod(timeframe)
  const dateXMap = buildDatesXMap(timeperiod)
  const dates = Object.keys(dateXMap)
  const today = calculateToday(memberActual, timeframe)
  const lastDate = last(dates)
  const defaultDate = today || lastDate
  const defaultX = dateXMap[defaultDate]
  const defaultItem = memberActual.find((item) => item.date === defaultDate)
  const defaultTooltips: Tooltip[] = !defaultItem ?
    [] :
    [{
      type: 'member',
      text: defaultItem.amount,
      x: defaultX,
      y: defaultItem.amount,
    }]


  const selectedMemberTrend = pickTimeframeTrend(memberActual, timeperiod)
  const selectedPeersTrend = buildApproximatedPeersTrend(peersActual, timeperiod, dateXMap, today)
  const selectedOthersTrend = buildApproximatedPeersTrend(othersActual, timeperiod, dateXMap, today)

  const dots = [
    ...buildDots(selectedMemberTrend, 'member', dateXMap, timeperiod),
    ...buildDots(selectedPeersTrend, 'peers', dateXMap, timeperiod),
    ...buildDots(selectedOthersTrend, 'others', dateXMap, timeperiod),
  ]

  return {
    memberItems: buildPoints(selectedMemberTrend, dateXMap),
    peersItems: buildPoints(selectedPeersTrend, dateXMap),
    otherItems: buildPoints(selectedOthersTrend, dateXMap),

    dots,

    xWidth: dates.length,
    defaultTooltips,

    dividers: isOneMonth ? buildDividersOneMonth(dateXMap) : buildDividersMultiMonth(dateXMap),
    sections: isOneMonth ? buildSectionsOneMonth(dateXMap) : buildSectionsMultiMonth(dateXMap),
    tips: buildTips(dateXMap, today),
    headerTips: buildTips(dateXMap, today, formatHeaderTipDate),

    valueDecorator: type === 'creditscore'
      ? creditScoreValueDecorator
      : formatUSCurrency,

    selectedPeergroupName,
    isMobile,

    withComparison,
  }
}

type TrendGenerator = (date: string) => number
export const generateTrendLine = (period: Timeperiod, generator: TrendGenerator): TrendLine => {
  const trendLine: TrendLine = []
  const lastDate = moment(period.end_date)

  for (
    const currentDate = moment(period.start_date);
    currentDate.valueOf() <= lastDate.valueOf();
    currentDate.add(1, 'day')
  ) {
    const date = currentDate.format(DATE_FORMAT_ISO_FULL)
    trendLine.push({
      date,
      amount: generator(date),
    })
  }

  return trendLine
}

// ===========================================================================//
//                                 RANK CHART                                 //
// ===========================================================================//

// RANK CHART HELPERS
export const findRank = (percentiles: number[], rankValue: number): number => {
  if (last(percentiles) < rankValue) {
    return 100
  }

  const upperBoundIndex = findLastIndex(percentiles, (_p, i) => {
    return (percentiles[i] === rankValue) ||
      (percentiles[i] > rankValue && (i > 0 ? percentiles[i - 1] < rankValue : true))
  })
  return upperBoundIndex + 1
}

export const RANK_CHART_WIDTH = 100
export const RANK_CHART_DIVIDERS = [1].concat(range(1, Math.round(RANK_CHART_WIDTH / 10) + 1).map((x) => x * 10))
export const RANK_CHART_TIPS = range(1, RANK_CHART_WIDTH + 1)
  .reduce((result, x) => {
    result[x] = `${ x }%`
    return result
  }, {} as NumericMap<string>)
export const RANK_CHART_SECTIONS: NumericMap<string> = {
  6: 'Bottom 10%',
  20: '20%',
  30: '30%',
  40: '40%',
  50: '50%',
  60: '60%',
  70: '70%',
  80: '80%',
  95: 'Top 10%',
}

export const percentileToItem = (y: number, idx: number) => ({
  x: idx + 1,
  y,
})

export const log = (value: number, base: number) =>
  Math.log(value) / Math.log(base)

// shouldn't be less than 1, because log(1) === 0
const LINEAR_BOUND = 1
const LINEAR_DISTANCE = 0.1
const LINEAR_RATIO = LINEAR_DISTANCE / LINEAR_BOUND
const LINEAR_LOG_AMEND = LINEAR_DISTANCE - Math.log10(LINEAR_BOUND)

const getLogCoord = (abs: number) => {
  // increase base of logarithm to compensate exaggeration of small values
  const base = 10 + 1000 / abs
  return log(abs, base)
}

export const rankScale: ScaleFunction = (value) => {
  const abs = Math.abs(value)
  const absCoord = abs < LINEAR_BOUND
    ? abs * LINEAR_RATIO
    : getLogCoord(abs) + LINEAR_LOG_AMEND
  return Math.sign(value) * absCoord
}

export const rescale = (from: Boundaries, to: Boundaries) => (scale: ScaleFunction): ScaleFunction => {
  const scaledFrom = from.map(scale)
  const ratio = (to[1] - to[0]) / (scaledFrom[1] - scaledFrom[0])
  return (value) => (scale(value) - scaledFrom[0]) * ratio + to[0]
}
